<template>
  <div v-if="$slots.trigger" @click="openModal">
    <slot name="trigger" />
  </div>
  <Teleport to="#teleports">
    <Transition :name="transition" mode="out-in">
      <div
        v-if="isOpen"
        :class="[
          'tw:fixed tw:inset-0 tw:flex tw:justify-center',
          { 'tw:items-stretch': maxHeight },
          { 'tw:items-center': !maxHeight },
          { 'tw:sm:p-4': !fullscreen },
          { 'tw:p-0': fullscreen },
          { 'tw:bg-black/50 tw:backdrop-brightness-50': isOverlayVisible }
        ]"
        :style="{
          'z-index': index,
          filter: frontBlur ? 'brightness(70%)' : undefined
        }"
        @click="emit('click')"
        @click.self="handleClickOutside"
      >
        <!-- TODO: set bg color variable instead direct hex code -->
        <div
          :class="[
            'tw:relative tw:flex tw:flex-col custom-scroll-container tw:sm:max-h-[100dvh]',
            {
              'tw:overflow-y-auto': scrollable,
              'tw:w-screen tw:h-full': fullscreen,
              'tw:rounded-xl tw:max-w-4xl tw:h-full tw:w-screen tw:sm:w-auto tw:sm:h-auto':
                !fullscreen && !disableFullHeight,
              'tw:rounded-xl tw:max-w-4xl tw:w-screen tw:sm:w-auto tw:sm:h-auto': !fullscreen && disableFullHeight,
              'tw:bg-[#0D242F]/75 tw:border-blue-8 tw:border': blured && !transparent,
              'tw:bg-blue-9': !blured && !transparent,
              'modal-shadow': !transparent,
              'tw:min-w-fit': disableMinWFit,
              'ru-locale': $i18n.locale === 'ru'
            }
          ]"
          :style="{
            filter: frontBlur ? 'brightness(50%)' : undefined,
            ...(heightFix ? { height: '100dvh', maxHeight: '960px' } : {})
          }"
        >
          <div
            v-if="isHeaderVisible"
            :class="[
              'tw:flex-shrink-0 tw:p-2 tw:sm:p-5 tw:sm:pb-2',
              {
                'tw:absolute tw:top-0 tw:inset-x-0 tw:z-10': fixHeader
              }
            ]"
            :style="
              headerTopOffset
                ? { marginTop: typeof headerTopOffset === 'number' ? `${headerTopOffset}px` : headerTopOffset }
                : undefined
            "
          >
            <div class="tw:z-[2] tw:flex tw:justify-between tw:items-center">
              <div class="tw:flex tw:items-center tw:gap-2 tw:mr-auto">
                <img
                  v-if="isTip && !isTipOpen"
                  class="tw:w-5 tw:h-5 tw:cursor-pointer"
                  src="/img/icons/info.svg"
                  @click="openTip"
                />
                <img
                  v-if="backButton"
                  class="tw:w-5 tw:h-5 tw:cursor-pointer"
                  src="/img/icons/back.svg"
                  @click="handleBackButton"
                />
              </div>
              <img
                v-if="closeButton"
                class="tw:ml-auto tw:w-5 tw:h-5 tw:cursor-pointer"
                src="/img/icons/bone_close.svg"
                @click="handleClose"
              />
            </div>
            <div v-if="$slots['tip-header']" v-show="isTipOpen">
              <slot name="tip-header" />
            </div>
            <template v-if="!isTipOpen">
              <slot name="header" />
            </template>
          </div>
          <div
            v-if="$slots['tip-content']"
            v-show="isTipOpen"
            :class="[
              'tw:flex-1 tw:min-h-0',
              {
                'tw:pt-16': fixHeader && isHeaderVisible
              }
            ]"
          >
            <slot name="tip-content" />
          </div>
          <div
            v-show="!isTipOpen"
            :class="[
              'tw:flex-1 tw:min-h-0',
              {
                'tw:overflow-x-hidden': !disableContentOverflowHidden,
                'tw:overflow-y-auto': !disableContentOverflowAuto
              }
            ]"
          >
            <slot />
          </div>
          <div v-if="$slots?.footer" class="tw:flex-shrink-0 tw:mt-auto tw:sm:mt-0 tw:p-6">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
const {
  transition = 'fade',
  closeButton = true,
  backButton = false,
  closeOnClickOutside = true,
  fullscreen = false,
  fixHeader = false,
  blured = false,
  transparent = false,
  frontBlur = false,
  scrollable = false,
  disableContentOverflowHidden = false,
  disableContentOverflowAuto = false,
  maxHeight = false,
  disableMinWFit = false,
  heightFix = false,
  disableFullHeight = false,
  indexOverride,
  headerTopOffset
} = defineProps<{
  transition?: string;
  closeButton?: boolean;
  backButton?: boolean;
  closeOnClickOutside?: boolean;
  fullscreen?: boolean;
  fixHeader?: boolean;
  blured?: boolean;
  frontBlur?: boolean;
  transparent?: boolean;
  scrollable?: boolean;
  disableContentOverflowHidden?: boolean;
  disableContentOverflowAuto?: boolean;
  maxHeight?: boolean;
  disableMinWFit?: boolean;
  heightFix?: boolean;
  disableFullHeight?: boolean;
  indexOverride?: number;
  headerTopOffset?: number | string;
}>();

const isOpen = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  back: [];
  closed: [];
  click: [];
}>();

const { pushModal, popModal, isFirstModal, getIndex } = useModalStackStore();
const uniqueModalId = useId();
const isOverlayVisible = computed(() => isFirstModal(uniqueModalId));
const index = computed(() => indexOverride || getIndex(uniqueModalId));

const bodyElement = computed<HTMLElement | null>(() => {
  if (import.meta.client && typeof document !== 'undefined') {
    return document.body;
  }
  return null;
});

const isScrollLocked = useScrollLock(bodyElement, false);

watchImmediate(
  () => isOpen.value,
  (value) => {
    if (value) {
      pushModal(uniqueModalId);
    } else {
      popModal(uniqueModalId);
    }
    if (import.meta.client) {
      nextTick(() => {
        if (isFirstModal(uniqueModalId)) {
          isScrollLocked.value = value;
        }
      });
    }
  }
);

const slots = useSlots();
const isHeaderVisible = computed(() => !!closeButton || !!backButton || slots?.header || slots?.tipHeader);
const isTip = computed(() => !!slots['tip-header'] || !!slots['tip-body']);
const isTipOpen = ref(false);

const openModal = () => {
  isOpen.value = true;
  pushModal(uniqueModalId);
};

const closeModal = () => {
  isOpen.value = false;
  emit('closed');
  popModal(uniqueModalId);
};

const openTip = () => (isTipOpen.value = true);
const closeTip = () => (isTipOpen.value = false);

function handleBackButton() {
  if (isTipOpen.value) {
    closeTip();
    return;
  }
  emit('back');
}

function handleClose() {
  if (isTipOpen.value) closeTip();
  else closeModal();
}

function handleClickOutside() {
  if (!closeOnClickOutside) return;
  closeModal();
}

onMounted(() => {
  const isScrollLocked = useScrollLock(document.body, false);

  watch(isOpen, (newValue) => {
    isScrollLocked.value = newValue;
  });
});

onUnmounted(() => {
  popModal(uniqueModalId);
});
</script>

<style scoped>
/* maybe move to shared transitions idk */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-shadow {
  box-shadow: 0px 5px 5px 5px rgba(0, 0, 0, 0.5);
}
</style>
